<template>
    <div
        class="KbArticleContent"
        :class="`KbArticleContent--${frame}`"
        @click="handleClick"
        v-html="sanitizedHtml"
    />
</template>

<style lang="scss">
@import "@/modules/kb/styles/kbContent";

.KbArticleContent {
    @include kbContent;

    &--panel {
        max-width: 100%;
    }

    &--page {
        max-width: 760px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import DOMPurify from 'dompurify';
    import { START_LOCATION } from 'vue-router';

    import { KbArticle } from '@evidentid/tprm-portal-lib/models/kb';

    import { useKbStore } from '@/modules/kb/store';

    import { kbLocation, parsePortalHelpPath, resolveInternalTarget } from './helpers';

    export default defineComponent({
        name: 'KbArticleContent',
        props: {
            article: {
                type: Object as PropType<KbArticle>,
                required: true,
            },
            frame: {
                type: String as PropType<'panel' | 'page'>,
                default: 'page',
            },
        },
        emits: [ 'navigate' ],
        computed: {
            kbStore() {
                return useKbStore();
            },
            // The backend already sanitizes. This pass is defence in depth, because the
            // result is handed to v-html.
            //
            // Sanitization contract for KbArticle and every model in tprm-portal-lib's
            // kb module: `article.html` here, and a search result's `snippet`, are the
            // only fields that ever go through a sanitizer and may be rendered as
            // markup. `title`, `name`, `description` and `sectionId` are raw upstream
            // strings the backend never runs through its HTML-safety pass -- render
            // those as text ({{ }} interpolation) everywhere, never with v-html.
            sanitizedHtml(): string {
                const fragment = DOMPurify.sanitize(this.article.html, {
                    ADD_TAGS: [ 'iframe', 'details', 'summary' ],
                    ADD_ATTR: [ 'allowfullscreen', 'loading', 'target', 'data-kb-external', 'data-kb-image' ],
                    RETURN_DOM_FRAGMENT: true,
                });
                fragment.querySelectorAll('a').forEach((anchor) => this.resolveLink(anchor));
                const container = document.createElement('div');
                container.appendChild(fragment);
                return container.innerHTML;
            },
        },
        watch: {
            // The images held for the article on screen follow the article, so the store can free
            // an article's object URLs once nothing is showing it.
            'article.id': {
                handler(id: string, previousId: string): void {
                    this.kbStore.releaseArticleImages(previousId);
                    this.kbStore.acquireArticleImages(id);
                },
            },
            // v-html swaps the whole subtree on every change, so images are looked up again each time.
            sanitizedHtml() {
                this.$nextTick(() => this.loadImages());
            },
        },
        created() {
            this.kbStore.acquireArticleImages(this.article.id);
        },
        mounted() {
            this.loadImages();
        },
        beforeUnmount() {
            this.kbStore.releaseArticleImages(this.article.id);
        },
        methods: {
            // The backend emits article images without a src, only the attachment id: an <img> request
            // carries no credentials, so the bytes are fetched through the store and attached as an
            // object URL. A failed image keeps its alt text and no src.
            loadImages(): void {
                const articleId = this.article.id;
                const root = this.$el as HTMLElement;
                root.querySelectorAll<HTMLImageElement>('img[data-kb-image]').forEach(async (image) => {
                    const url = await this.kbStore.loadArticleImage(articleId, image.getAttribute('data-kb-image')!);
                    // The reader may have moved to another article (or the markup re-rendered) while the
                    // request was out; the image this response was for is then no longer in the page.
                    if (url && this.article.id === articleId && root.contains(image)) {
                        image.setAttribute('src', url);
                    }
                });
            },
            // The backend can only emit root-relative `/help/...` paths: it doesn't know the
            // portal routes by hash with the account id in front. Left as is, a plain click
            // still works (intercepted below), but a middle-click, a copied link or "open in
            // new tab" lands on a URL the portal can't serve. Giving every in-portal link its
            // real router href fixes those; the target is stamped on the anchor too, so the
            // click handler doesn't have to parse a router-specific href back apart.
            resolveLink(anchor: Element): void {
                // Only attributes stamped below may reach resolveInternalTarget; a forged one in the markup must not,
                // including on an anchor with no href, which the click handler would still match.
                // `data-kb-image` is deliberately not in this list: the backend stamps it (after stripping any
                // copy in the Zendesk HTML) and only serves ids it recorded for this article, so removing it
                // here would just blank real images.
                [ 'data-kb-kind', 'data-kb-id', 'data-kb-fragment' ].forEach((name) => anchor.removeAttribute(name));
                const href = anchor.getAttribute('href') || '';
                if (!href) {
                    return;
                }
                const target = parsePortalHelpPath(href);
                if (target) {
                    // An explicit base keeps router.resolve from reading the current route, which would make
                    // the sanitized markup recompute on every navigation. The named routes are tenant-free,
                    // so the result doesn't depend on where the reader is.
                    anchor.setAttribute('href', this.$router.resolve(kbLocation(target), START_LOCATION).href);
                    anchor.setAttribute('data-kb-kind', target.kind);
                    if (target.kind !== 'home') {
                        anchor.setAttribute('data-kb-id', target.id);
                    }
                    if (target.kind === 'article' && target.fragment) {
                        anchor.setAttribute('data-kb-fragment', target.fragment);
                    }
                    return;
                }
                // The backend can't tell the portal's own host from any other, so a link to the
                // host this page is served from arrives marked external. It is not: open it in
                // place like any other in-product link.
                if (this.isSameOrigin(href)) {
                    anchor.removeAttribute('target');
                    anchor.removeAttribute('rel');
                    anchor.removeAttribute('data-kb-external');
                }
            },
            isSameOrigin(href: string): boolean {
                try {
                    return new URL(href, window.location.href).host === window.location.host
                        && /^https?:/.test(href);
                } catch {
                    return false;
                }
            },
            handleClick(event: MouseEvent): void {
                // A modified click is the reader asking the browser for a new tab or window;
                // the anchor's href now supports that, so let it through.
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                }
                const target = resolveInternalTarget(event.target as HTMLElement);
                if (target) {
                    event.preventDefault();
                    this.$emit('navigate', target);
                }
            },
        },
    });
</script>
